import axios from 'axios';
import sha1 from 'sha1';
import { reactive } from 'vue';
import { ApiConfig } from '@/shared/constants/ApiConfig';
import { setDefaultCountryId } from '@/shared/models/Country';
import serviceEvents, { UserLoggedIn, UserLoggedOut } from '@/shared/services/events/serviceEvents';
import { present } from '@/shared/utils/typescript';
import AuthClient from '../client/AuthClient';
import AuthenticationResult from '../models/AuthenticationResult';
import Identity from '../models/Identity';
import AzureAuthService, { AzureAccessToken } from './AzureAuthService';
import { goto } from '../router/navigation';
import router from '../router';

const PUBLIC_KEY = 'auth_uniqueKey';

// remove legacy keys
localStorage.removeItem('change_password');
localStorage.removeItem('auth_permissions');
localStorage.removeItem('auth_id');

export class PasswordExpired extends Error {}

class AuthService {
  private currentUser: Identity | null = null;
  private isLoggingOut = false;

  getCurrentUser(): Identity {
    return present(this.currentUser, 'no current user');
  }

  logout() {
    this.isLoggingOut = true;
    goto({ name: 'login' }, router);
  }

  isAuthenticated(): boolean {
    return this.currentUser !== null && !this.isLoggingOut;
  }

  async updatePassword(password: string): Promise<void> {
    await AuthClient.updatePassword(password);
  }

  setIsLoggingOut(status: boolean) {
    this.isLoggingOut = status;
  }

  removeCurrentUser() {
    localStorage.clear();
    sessionStorage.clear();

    this.currentUser = null;

    delete axios.defaults.headers.common.PublicKey;
    delete axios.defaults.headers.common['Authorization-token'];

    serviceEvents.fire(new UserLoggedOut());
  }

  async init(): Promise<void> {
    const token = await AzureAuthService.handleRedirect();
    if (token) {
      await this.handleAzureLogin(token);
    } else {
      const publicKey = localStorage.getItem(PUBLIC_KEY);
      if (publicKey) await this.afterLogin(publicKey);
    }
  }

  async login(username: string, password: string): Promise<void> {
    const result = await AuthClient.authenticate(username, password);
    if (result.changePassword) {
      // the API to update the password requires auth headers
      this.setHeaders(result.publicKey);
      throw new PasswordExpired();
    }
    await this.handleLogin(result);
  }

  async loginWithAzure(): Promise<void> {
    const token = await AzureAuthService.login();
    await this.handleAzureLogin(token);
  }

  private async handleAzureLogin(token: AzureAccessToken): Promise<void> {
    const result = await AuthClient.authenticateWithAzure(token);
    await this.handleLogin(result);
  }

  private async handleLogin(result: AuthenticationResult): Promise<void> {
    localStorage.setItem(PUBLIC_KEY, result.publicKey);
    await this.afterLogin(result.publicKey);
  }

  private async afterLogin(publicKey: string): Promise<void> {
    this.setHeaders(publicKey);
    this.currentUser = await AuthClient.getCurrentIdentity();
    setDefaultCountryId(this.currentUser.countryId);
    await serviceEvents.fire(new UserLoggedIn(this.currentUser));
  }

  private setHeaders(publicKey: string): void {
    axios.defaults.headers.common.PublicKey = publicKey;
    const privatekey = ApiConfig.API_PRIVATE_KEY;
    axios.defaults.headers.common['Authorization-token'] = sha1(publicKey + privatekey);
  }
}

export default reactive(new AuthService());